import React, { useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, useScroll } from 'framer-motion';
import { 
  Sparkles, ArrowRight, Brain, FileText, Zap, Shield, Clock, 
  TrendingUp, Users, Target, CheckCircle, MessageSquare,
  Globe, Lock, Award, ChevronDown, Volume2, VolumeX, Play
} from 'lucide-react';

// Custom type for framer-motion variants to fix type compatibility
type MotionVariants = {
  [key: string]: {
    [key: string]: any;
  };
};

const LexyPresentation: React.FC = () => {
  const navigate = useNavigate();
  const containerRef = useRef<HTMLDivElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const [hasStarted, setHasStarted] = useState(false);
  const [isMuted, setIsMuted] = useState(true);
  const { scrollYProgress } = useScroll({ container: containerRef });

  const startVideoWithSound = () => {
    if (videoRef.current) {
      videoRef.current.currentTime = 0;
      videoRef.current.muted = false;
      videoRef.current.play();
      setHasStarted(true);
      setIsMuted(false);
    }
  };

  const toggleMute = () => {
    if (videoRef.current) {
      videoRef.current.muted = !videoRef.current.muted;
      setIsMuted(!isMuted);
    }
  };

  const fadeInUp: MotionVariants = {
    hidden: { opacity: 0, y: 40 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.8, ease: 'easeOut' } }
  };

  const staggerContainer: MotionVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.15 }
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-indigo-950">
      {/* Fixed Navbar - Full Width */}
      <header className="fixed top-0 w-full z-50 bg-slate-950/80 backdrop-blur-xl border-b border-white/10">
        <div className="w-full px-6 lg:px-12 h-16 flex items-center justify-between">
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            className="flex items-center gap-3 cursor-pointer"
            onClick={() => navigate('/lexy')}
          >
            <div className="w-8 h-8 bg-gradient-to-br from-violet-500 to-purple-600 rounded-lg flex items-center justify-center">
              <Sparkles className="w-4 h-4 text-white" />
            </div>
            <span className="text-xl font-bold text-white">Lexy</span>
            <span className="hidden sm:inline text-sm text-white/40 ml-2">| Presentation</span>
          </motion.div>

          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate('/lexy/login')}
              className="hidden sm:block px-4 py-2 text-sm font-medium text-white/70 hover:text-white transition-colors"
            >
              Log in
            </button>
            <button
              onClick={() => navigate('/lexy/signup')}
              className="bg-gradient-to-r from-violet-500 to-purple-600 text-white px-6 py-2.5 rounded-lg font-semibold text-sm shadow-lg hover:shadow-purple-500/50 transition-all hover:scale-105"
            >
              Start free
            </button>
          </div>
        </div>
      </header>

      {/* Progress Bar */}
      <motion.div 
        className="fixed top-16 left-0 right-0 h-1 bg-white/10 z-40"
        style={{ scaleX: scrollYProgress, transformOrigin: '0%' }}
      >
        <div className="h-full bg-gradient-to-r from-violet-500 to-purple-600" />
      </motion.div>

      {/* Main Content */}
      <div ref={containerRef} className="pt-16">
        
        {/* SLIDE 1: Hero with Video */}
        <section className="min-h-screen flex items-center justify-center px-6 py-20 relative overflow-hidden">
          <div className="absolute top-20 right-10 w-96 h-96 bg-purple-500/20 rounded-full blur-3xl animate-pulse pointer-events-none" />
          <div className="absolute bottom-20 left-10 w-96 h-96 bg-indigo-500/20 rounded-full blur-3xl animate-pulse pointer-events-none" style={{ animationDelay: '1s' }} />
          
          <div className="max-w-7xl mx-auto w-full relative z-10">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <motion.div
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                variants={staggerContainer}
              >
                <motion.div variants={fadeInUp} className="inline-flex items-center gap-2 bg-purple-500/10 border border-purple-500/20 rounded-full px-4 py-2 mb-6">
                  <Sparkles className="w-4 h-4 text-purple-400" />
                  <span className="text-sm font-medium text-purple-300">AI for lawyers</span>
                </motion.div>
                
                <motion.h1 variants={fadeInUp} className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight">
                  The future
                  <br />
                  <span className="text-transparent bg-clip-text bg-gradient-to-r from-violet-400 via-purple-400 to-indigo-400">
                    law firm
                  </span>
                </motion.h1>
                
                <motion.p variants={fadeInUp} className="text-lg text-white/60 mb-8 leading-relaxed">
                  Lexy is the AI platform that makes lawyers 10x more productive. 
                  From research to document production - in minutes, not hours.
                </motion.p>

                <motion.div variants={fadeInUp} className="flex flex-wrap gap-4">
                  <button
                    onClick={() => navigate('/lexy/signup')}
                    className="group bg-gradient-to-r from-violet-500 to-purple-600 text-white px-8 py-4 rounded-xl font-semibold shadow-lg hover:shadow-purple-500/50 transition-all hover:scale-105 flex items-center gap-2"
                  >
                    Get started
                    <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                  </button>
                </motion.div>
              </motion.div>

              {/* Video - 9:16 portrait format */}
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8, delay: 0.2 }}
                className="relative flex justify-center"
              >
                <div className="relative rounded-2xl overflow-hidden border border-white/10 shadow-2xl shadow-purple-500/20 bg-gradient-to-br from-violet-900/50 to-purple-900/50 max-w-[360px] w-full">
                  <video
                    ref={videoRef}
                    autoPlay
                    loop
                    muted
                    playsInline
                    preload="auto"
                    className="w-full object-cover relative z-10"
                    style={{ aspectRatio: '9/16' }}
                    src="https://customer-assets.emergentagent.com/job_legal-desk-app/artifacts/39hqyw1m_AI%20video%20(2).mp4"
                  />
                  
                  {!hasStarted && (
                    <motion.button
                      initial={{ scale: 0.9, opacity: 0 }}
                      animate={{ scale: 1, opacity: 1 }}
                      whileHover={{ scale: 1.1 }}
                      whileTap={{ scale: 0.95 }}
                      onClick={startVideoWithSound}
                      className="absolute inset-0 z-20 flex items-center justify-center cursor-pointer group"
                    >
                      <div className="absolute inset-0 bg-black/30 group-hover:bg-black/40 transition-colors" />
                      <div className="relative w-20 h-20 bg-white/20 backdrop-blur-xl border border-white/30 rounded-full flex items-center justify-center group-hover:bg-white/30 transition-all shadow-2xl">
                        <Play className="w-8 h-8 text-white ml-1" fill="white" />
                      </div>
                      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 bg-black/60 backdrop-blur-sm rounded-full px-4 py-2 text-sm text-white/90 whitespace-nowrap">
                        Click to play with sound
                      </div>
                    </motion.button>
                  )}
                  
                  {hasStarted && (
                    <motion.button
                      initial={{ opacity: 0, scale: 0.8 }}
                      animate={{ opacity: 1, scale: 1 }}
                      onClick={toggleMute}
                      className="absolute bottom-4 right-4 z-20 w-12 h-12 bg-black/50 backdrop-blur-xl border border-white/20 rounded-full flex items-center justify-center hover:bg-black/70 transition-all group"
                    >
                      {isMuted ? (
                        <VolumeX className="w-5 h-5 text-white group-hover:scale-110 transition-transform" />
                      ) : (
                        <Volume2 className="w-5 h-5 text-white group-hover:scale-110 transition-transform" />
                      )}
                    </motion.button>
                  )}
                </div>
              </motion.div>
            </div>
          </div>

          <motion.div 
            className="absolute bottom-8 left-1/2 -translate-x-1/2"
            animate={{ y: [0, 10, 0] }}
            transition={{ repeat: Infinity, duration: 2 }}
          >
            <ChevronDown className="w-8 h-8 text-white/40" />
          </motion.div>
        </section>

        {/* SLIDE 2: The Problem */}
        <section className="min-h-screen flex items-center justify-center px-6 py-20 relative">
          <div className="absolute inset-0 bg-gradient-to-b from-transparent via-red-950/10 to-transparent pointer-events-none" />
          
          <div className="max-w-5xl mx-auto w-full text-center relative z-10">
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-100px" }}
              variants={staggerContainer}
            >
              <motion.span variants={fadeInUp} className="text-red-400 font-medium text-sm uppercase tracking-wider mb-4 block">
                The Problem
              </motion.span>
              
              <motion.h2 variants={fadeInUp} className="text-4xl sm:text-5xl font-bold text-white mb-4">
                A lawyer's daily reality
              </motion.h2>
              
              <motion.p variants={fadeInUp} className="text-xl text-white/60 mb-16">
                Hours disappear. Margins shrink. Stress increases.
              </motion.p>

              <motion.div variants={staggerContainer} className="grid sm:grid-cols-2 gap-6">
                {[
                  { icon: Clock, text: '60% of time goes to admin and research', color: 'text-red-400', bg: 'bg-red-500/10', border: 'border-red-500/20' },
                  { icon: FileText, text: 'Hourly billing rewards inefficiency', color: 'text-orange-400', bg: 'bg-orange-500/10', border: 'border-orange-500/20' },
                  { icon: Users, text: 'Hard to scale without hiring', color: 'text-yellow-400', bg: 'bg-yellow-500/10', border: 'border-yellow-500/20' },
                  { icon: Target, text: 'Clients expect faster responses', color: 'text-amber-400', bg: 'bg-amber-500/10', border: 'border-amber-500/20' },
                ].map((item, idx) => (
                  <motion.div
                    key={idx}
                    variants={fadeInUp}
                    className={`${item.bg} border ${item.border} rounded-xl p-6 flex items-center gap-4 text-left`}
                  >
                    <div className="w-12 h-12 rounded-lg bg-white/5 flex items-center justify-center flex-shrink-0">
                      <item.icon className={`w-6 h-6 ${item.color}`} />
                    </div>
                    <span className="text-white text-lg">{item.text}</span>
                  </motion.div>
                ))}
              </motion.div>
            </motion.div>
          </div>
        </section>

        {/* SLIDE 3: The Solution */}
        <section className="min-h-screen flex items-center justify-center px-6 py-20 relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-b from-transparent via-purple-950/20 to-transparent pointer-events-none" />
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-purple-500/20 rounded-full blur-3xl pointer-events-none" />
          
          <div className="max-w-4xl mx-auto w-full text-center relative z-10">
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-100px" }}
              variants={staggerContainer}
            >
              <motion.span variants={fadeInUp} className="text-purple-400 font-medium text-sm uppercase tracking-wider mb-4 block">
                The Solution
              </motion.span>
              
              <motion.div variants={fadeInUp} className="flex justify-center mb-8">
                <div className="w-24 h-24 bg-gradient-to-br from-violet-500 to-purple-600 rounded-2xl flex items-center justify-center shadow-2xl shadow-purple-500/30">
                  <Sparkles className="w-12 h-12 text-white" />
                </div>
              </motion.div>

              <motion.h2 variants={fadeInUp} className="text-5xl sm:text-6xl font-bold text-white mb-6">
                Meet <span className="text-transparent bg-clip-text bg-gradient-to-r from-violet-400 to-purple-400">Lexy</span>
              </motion.h2>
              
              <motion.p variants={fadeInUp} className="text-xl text-white/60 mb-4">
                Your AI-powered legal assistant
              </motion.p>

              <motion.p variants={fadeInUp} className="text-lg text-white/50 max-w-2xl mx-auto leading-relaxed">
                Lexy isn't just a tool. It's a transformation of how you practice law. 
                AI that understands context, learns from you, and delivers results.
              </motion.p>
            </motion.div>
          </div>
        </section>

        {/* SLIDE 4-6: Feature Showcases */}
        {[
          {
            title: 'AI Case Intelligence',
            description: 'Lexy reads all case history, relevant legislation and similar cases. In seconds, the AI analyzes thousands of documents.',
            metric: '95% faster research',
            icon: Brain,
            gradient: 'from-violet-500 to-purple-600',
            points: ['Automatic analysis of new cases', 'Comparison with previous rulings', 'Real-time risk assessment', 'Legal strategy suggestions']
          },
          {
            title: 'Automatic document production',
            description: 'From intake to finished contract - AI handles the entire workflow. You approve and customize.',
            metric: '2 min vs 4 hours',
            icon: FileText,
            gradient: 'from-indigo-500 to-blue-600',
            points: ['Contract generation', 'Automatic letters to opposing counsel', 'Case preparation with AI', 'Quality assurance included']
          },
          {
            title: 'Value-based pricing',
            description: 'Set fixed prices per case type. Get paid for RESULTS, not hours. Clients love predictability.',
            metric: '40% higher margin',
            icon: TrendingUp,
            gradient: 'from-emerald-500 to-teal-600',
            points: ['Fixed price per case type', 'Transparent for clients', 'Higher profitability', 'Better client experience']
          },
        ].map((feature, idx) => (
          <section key={idx} className="min-h-screen flex items-center justify-center px-6 py-20 relative">
            <div className="max-w-6xl mx-auto w-full">
              <div className={`grid lg:grid-cols-2 gap-12 items-center ${idx % 2 === 1 ? 'lg:flex-row-reverse' : ''}`}>
                <motion.div
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true, margin: "-100px" }}
                  variants={staggerContainer}
                  className={idx % 2 === 1 ? 'lg:order-2' : ''}
                >
                  <motion.div variants={fadeInUp} className={`w-16 h-16 bg-gradient-to-br ${feature.gradient} rounded-2xl flex items-center justify-center mb-6 shadow-lg`}>
                    <feature.icon className="w-8 h-8 text-white" />
                  </motion.div>
                  
                  <motion.h2 variants={fadeInUp} className="text-4xl sm:text-5xl font-bold text-white mb-4">
                    {feature.title}
                  </motion.h2>
                  
                  <motion.p variants={fadeInUp} className="text-lg text-white/60 mb-6 leading-relaxed">
                    {feature.description}
                  </motion.p>

                  <motion.div variants={fadeInUp} className={`inline-flex items-center gap-2 bg-gradient-to-r ${feature.gradient} bg-opacity-20 rounded-full px-4 py-2 mb-8`}>
                    <Zap className="w-4 h-4 text-white" />
                    <span className="text-white font-semibold">{feature.metric}</span>
                  </motion.div>

                  <motion.ul variants={staggerContainer} className="space-y-3">
                    {feature.points.map((point, i) => (
                      <motion.li key={i} variants={fadeInUp} className="flex items-center gap-3 text-white/70">
                        <CheckCircle className="w-5 h-5 text-emerald-400 flex-shrink-0" />
                        <span>{point}</span>
                      </motion.li>
                    ))}
                  </motion.ul>
                </motion.div>

                <motion.div
                  initial={{ opacity: 0, scale: 0.9 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true, margin: "-100px" }}
                  transition={{ duration: 0.8 }}
                  className={`relative ${idx % 2 === 1 ? 'lg:order-1' : ''}`}
                >
                  <div className={`aspect-square max-w-md mx-auto bg-gradient-to-br ${feature.gradient} rounded-3xl p-1`}>
                    <div className="w-full h-full bg-slate-900 rounded-3xl flex items-center justify-center">
                      <feature.icon className="w-32 h-32 text-white/20" />
                    </div>
                  </div>
                  <div className={`absolute inset-0 bg-gradient-to-br ${feature.gradient} opacity-20 blur-3xl -z-10`} />
                </motion.div>
              </div>
            </div>
          </section>
        ))}

        {/* SLIDE 7: Workflow */}
        <section className="min-h-screen flex items-center justify-center px-6 py-20 relative">
          <div className="max-w-6xl mx-auto w-full">
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-100px" }}
              variants={staggerContainer}
              className="text-center mb-16"
            >
              <motion.span variants={fadeInUp} className="text-cyan-400 font-medium text-sm uppercase tracking-wider mb-4 block">
                Workflow
              </motion.span>
              <motion.h2 variants={fadeInUp} className="text-4xl sm:text-5xl font-bold text-white">
                How Lexy works
              </motion.h2>
            </motion.div>

            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-100px" }}
              variants={staggerContainer}
              className="relative"
            >
              <div className="hidden lg:block absolute top-1/2 left-0 right-0 h-0.5 bg-gradient-to-r from-violet-500 via-purple-500 to-cyan-500 -translate-y-1/2" />
              
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-6">
                {[
                  { num: '01', title: 'New case', desc: 'Comes in via email or portal', icon: MessageSquare, color: 'from-violet-500 to-purple-600' },
                  { num: '02', title: 'AI analyzes', desc: 'Lexy reads and categorizes', icon: Brain, color: 'from-purple-500 to-indigo-600' },
                  { num: '03', title: 'Suggestions', desc: 'Strategy, documents, pricing', icon: Target, color: 'from-indigo-500 to-blue-600' },
                  { num: '04', title: 'Approval', desc: 'You QA in seconds', icon: CheckCircle, color: 'from-blue-500 to-cyan-600' },
                  { num: '05', title: 'Delivery', desc: 'Client receives professional result', icon: Users, color: 'from-cyan-500 to-teal-600' },
                ].map((step, idx) => (
                  <motion.div
                    key={idx}
                    variants={fadeInUp}
                    className="relative"
                  >
                    <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-6 text-center h-full hover:bg-white/10 transition-colors">
                      <div className={`w-14 h-14 bg-gradient-to-br ${step.color} rounded-xl flex items-center justify-center mx-auto mb-4 shadow-lg`}>
                        <step.icon className="w-7 h-7 text-white" />
                      </div>
                      <div className="text-white/40 text-sm font-mono mb-2">{step.num}</div>
                      <h3 className="text-white font-bold text-lg mb-2">{step.title}</h3>
                      <p className="text-white/50 text-sm">{step.desc}</p>
                    </div>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          </div>
        </section>

        {/* SLIDE 8: Metrics */}
        <section className="min-h-screen flex items-center justify-center px-6 py-20 relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-b from-transparent via-indigo-950/30 to-transparent pointer-events-none" />
          
          <div className="max-w-5xl mx-auto w-full text-center relative z-10">
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-100px" }}
              variants={staggerContainer}
            >
              <motion.span variants={fadeInUp} className="text-indigo-400 font-medium text-sm uppercase tracking-wider mb-4 block">
                Results
              </motion.span>
              
              <motion.h2 variants={fadeInUp} className="text-4xl sm:text-5xl font-bold text-white mb-16">
                Results that matter
              </motion.h2>

              <motion.div variants={staggerContainer} className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
                {[
                  { value: '95%', label: 'Faster document production', icon: Zap, color: 'from-violet-500 to-purple-600' },
                  { value: '40%', label: 'Higher profitability per case', icon: TrendingUp, color: 'from-emerald-500 to-teal-600' },
                  { value: '20h', label: 'Saved per week', icon: Clock, color: 'from-blue-500 to-cyan-600' },
                  { value: '3x', label: 'More clients without hiring', icon: Users, color: 'from-orange-500 to-amber-600' },
                ].map((metric, idx) => (
                  <motion.div
                    key={idx}
                    variants={fadeInUp}
                    className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-8 hover:bg-white/10 transition-colors group"
                  >
                    <div className={`w-14 h-14 bg-gradient-to-br ${metric.color} rounded-xl flex items-center justify-center mx-auto mb-6 shadow-lg group-hover:scale-110 transition-transform`}>
                      <metric.icon className="w-7 h-7 text-white" />
                    </div>
                    <div className="text-5xl font-bold text-white mb-2">{metric.value}</div>
                    <div className="text-white/60 text-sm">{metric.label}</div>
                  </motion.div>
                ))}
              </motion.div>
            </motion.div>
          </div>
        </section>

        {/* SLIDE 9: Security */}
        <section className="min-h-screen flex items-center justify-center px-6 py-20 relative">
          <div className="max-w-5xl mx-auto w-full text-center">
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-100px" }}
              variants={staggerContainer}
            >
              <motion.span variants={fadeInUp} className="text-emerald-400 font-medium text-sm uppercase tracking-wider mb-4 block">
                Security
              </motion.span>
              
              <motion.h2 variants={fadeInUp} className="text-4xl sm:text-5xl font-bold text-white mb-4">
                Security first
              </motion.h2>
              
              <motion.p variants={fadeInUp} className="text-xl text-white/60 mb-16">
                Bank-grade security. GDPR-compliant. European servers.
              </motion.p>

              <motion.div variants={staggerContainer} className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
                {[
                  { icon: Lock, title: 'Encrypted data', desc: 'End-to-end encryption', color: 'from-emerald-500 to-teal-600' },
                  { icon: Shield, title: 'GDPR-compliant', desc: 'Full privacy protection', color: 'from-blue-500 to-cyan-600' },
                  { icon: Globe, title: 'European servers', desc: 'Data stored in EU', color: 'from-violet-500 to-purple-600' },
                  { icon: Award, title: 'ISO 27001', desc: 'Certified security', color: 'from-amber-500 to-orange-600' },
                ].map((item, idx) => (
                  <motion.div
                    key={idx}
                    variants={fadeInUp}
                    className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-6 hover:bg-white/10 transition-colors"
                  >
                    <div className={`w-14 h-14 bg-gradient-to-br ${item.color} rounded-xl flex items-center justify-center mx-auto mb-4 shadow-lg`}>
                      <item.icon className="w-7 h-7 text-white" />
                    </div>
                    <h3 className="text-white font-bold text-lg mb-2">{item.title}</h3>
                    <p className="text-white/50 text-sm">{item.desc}</p>
                  </motion.div>
                ))}
              </motion.div>
            </motion.div>
          </div>
        </section>

        {/* SLIDE 10: CTA */}
        <section className="min-h-screen flex items-center justify-center px-6 py-20 relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-t from-purple-950/30 via-transparent to-transparent pointer-events-none" />
          <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-[800px] h-[400px] bg-purple-500/20 rounded-full blur-3xl pointer-events-none" />
          
          <div className="max-w-3xl mx-auto w-full text-center relative z-10">
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-100px" }}
              variants={staggerContainer}
            >
              <motion.div variants={fadeInUp} className="w-20 h-20 bg-gradient-to-br from-violet-500 to-purple-600 rounded-2xl flex items-center justify-center mx-auto mb-8 shadow-2xl shadow-purple-500/30">
                <Sparkles className="w-10 h-10 text-white" />
              </motion.div>
              
              <motion.h2 variants={fadeInUp} className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6">
                Ready for the future?
              </motion.h2>
              
              <motion.p variants={fadeInUp} className="text-xl text-white/60 mb-10">
                Join thousands of lawyers already using Lexy to transform their practice.
              </motion.p>

              <motion.div variants={fadeInUp} className="flex flex-col sm:flex-row gap-4 justify-center">
                <button
                  onClick={() => navigate('/lexy/signup')}
                  className="group bg-gradient-to-r from-violet-500 to-purple-600 text-white px-10 py-5 rounded-xl font-semibold text-lg shadow-lg hover:shadow-purple-500/50 transition-all hover:scale-105 flex items-center justify-center gap-3"
                >
                  Get started free
                  <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                </button>
                <button
                  onClick={() => navigate('/lexy')}
                  className="bg-white/5 backdrop-blur-sm border border-white/10 text-white px-10 py-5 rounded-xl font-semibold text-lg hover:bg-white/10 transition-all"
                >
                  Back to homepage
                </button>
              </motion.div>

              <motion.p variants={fadeInUp} className="text-white/40 text-sm mt-8">
                No credit card required • Free trial • Cancel anytime
              </motion.p>
            </motion.div>
          </div>
        </section>

      </div>

      {/* Footer */}
      <footer className="border-t border-white/10 py-8 px-6">
        <div className="w-full px-6 lg:px-12 flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="w-6 h-6 bg-gradient-to-br from-violet-500 to-purple-600 rounded-md flex items-center justify-center">
              <Sparkles className="w-3 h-3 text-white" />
            </div>
            <span className="text-white/60 text-sm">© 2025 Lexy. All rights reserved.</span>
          </div>
          <div className="flex items-center gap-6 text-sm text-white/40">
            <button type="button" className="hover:text-white/60 transition-colors">Privacy</button>
            <button type="button" className="hover:text-white/60 transition-colors">Terms</button>
            <button type="button" className="hover:text-white/60 transition-colors">Contact</button>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LexyPresentation;
